import React from 'react'
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

interface DashboardRnStats {
  total_count: number
  new_rn_count: number
  old_rn_count: number
  not_defined_rn_count: number
}

interface NewRNAppsChartProps {
  data: DashboardRnStats
  colors?: Array<string>
  title: string
}

interface LabelProps {
  cx: number
  cy: number
  midAngle: number
  innerRadius: number
  outerRadius: number
  percent: number
  payload: { value: number; name: string }
}

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  outerRadius,
  percent,
  payload,
}: LabelProps) => {
  const value = payload.value
  if (!value || percent <= 0.01) return null

  const RADIAN = Math.PI / 180
  const sin = Math.sin(-midAngle * RADIAN)
  const cos = Math.cos(-midAngle * RADIAN)

  const lineLength = 20
  const sx = cx + outerRadius * cos
  const sy = cy + outerRadius * sin
  const ex = cx + (outerRadius + lineLength) * cos
  const ey = cy + (outerRadius + lineLength) * sin

  const textOffset = 4
  const textX = ex + (cos >= 0 ? textOffset : -textOffset)
  const textY = ey

  const textAnchor = cos >= 0 ? 'start' : 'end'

  return (
    <>
      <polyline
        points={`${sx},${sy} ${ex},${ey}`}
        stroke="#8884d8"
        fill="none"
      />
      <text
        x={textX}
        y={textY}
        textAnchor={textAnchor}
        dominantBaseline="central"
        fill="#333"
      >
        <tspan x={textX} dy={-6} fontSize={12} fontWeight={600}>
          {`${value} app${value === 1 ? '' : 's'}`}
        </tspan>
        <tspan x={textX} dy={16} fontSize={11}>
          {`${Math.round(percent * 100)}%`}
        </tspan>
      </text>
    </>
  )
}

const NewRNAppsChart: React.FC<NewRNAppsChartProps> = ({
  data,
  colors = ['#FFBB28', '#0088FE', '#ea6c57'],
  title,
}) => {
  const options = [
    { name: 'RN 0.77.2 (New)', value: data.new_rn_count },
    { name: 'RN 0.74.2 (Old)', value: data.old_rn_count },
    { name: 'Default (Old)', value: data.not_defined_rn_count },
  ].filter(entry => entry.value > 0)

  return (
    <div className="chart-container">
      <h2>{title}</h2>
      <p>Total apps: {data.total_count}</p>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={options}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomizedLabel}
            outerRadius={100}
            fill="#8884d8"
            dataKey="value"
            key={Math.random()}
          >
            {options.map((entry, index) => (
              <Cell key={entry.name} fill={colors[index % colors.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  )
}

export default NewRNAppsChart
